import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Table } from '@fikasio/react-ui-components';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import Form from 'react-bootstrap/Form';
import { useTranslation } from 'react-i18next';
import Page404 from 'pages/Page404';
import { FEATURE_SECTIONS, FeatureId } from 'services/features/catalog';
import { useGetFeaturesQuery, usePatchFeaturesMutation } from 'services/features/api';
import { useHasPermission } from 'services/login/hooks';
import links from 'utils/links';
import { PERMISSIONS } from 'utils/permissions';
import { spaLinkProps } from 'utils/spaLink';
import styles from './FeaturesPage.module.css';

const FeaturesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageFeatures = useHasPermission(PERMISSIONS.FEATURES_MANAGE);
  const { data: features, isError: failedToLoad } = useGetFeaturesQuery(undefined, {
    skip: !canManageFeatures,
  });
  const [patchFeatures, { isError: failedToUpdate, isLoading: isUpdating }] = usePatchFeaturesMutation();

  useEffect(() => {
    if (!canManageFeatures) {
      return;
    }
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname,
    });
  }, [canManageFeatures, location.pathname]);

  if (!canManageFeatures) {
    return <Page404 />;
  }

  const toggleFeature = (id: FeatureId, enabled: boolean) => {
    patchFeatures({ [id]: enabled });
  };

  return (
    <>
      <Helmet>
        <title>{`${t('features')} · ${t('admin')}`}</title>
      </Helmet>
      <Breadcrumb
        items={[
          { ...spaLinkProps(links.paths.home, navigate), label: t('home') },
          { ...spaLinkProps(links.paths.admin, navigate), label: t('admin') },
          { label: t('features') },
        ]}
      />
      <h4>{t('features')}</h4>
      <p className={styles.intro}>{t('featuresIntro')}</p>
      {
        failedToLoad && (
          <p className={styles.error} role="alert">{t('featuresLoadFailed')}</p>
        )
      }
      {
        failedToUpdate && (
          <p className={styles.error} role="alert">{t('featureUpdateFailed')}</p>
        )
      }
      <Table
        bordered
        className={styles.table}
        hover
        responsive
      >
        <thead>
          <tr>
            <th>{t('name')}</th>
            <th>{t('featureKey')}</th>
            <th>{t('description')}</th>
            <th className={styles.switchColumn} />
          </tr>
        </thead>
        <tbody>
          {
            FEATURE_SECTIONS.flatMap(section => {
              const rows = section.features.map(feature => {
                const descriptionId = `feature-${feature.id}-description`;
                const enabled = Boolean(features?.[feature.id]);

                return (
                  <tr key={feature.id}>
                    <td>
                      <label className={styles.name} htmlFor={`feature-${feature.id}`}>
                        {t(feature.labelKey)}
                      </label>
                    </td>
                    <td>
                      <code className={styles.key}>{feature.id}</code>
                    </td>
                    <td>
                      <p className={styles.description} id={descriptionId}>
                        {t(feature.descriptionKey)}
                      </p>
                    </td>
                    <td className={styles.switchColumn}>
                      <Form.Check
                        aria-describedby={descriptionId}
                        checked={enabled}
                        disabled={!features || isUpdating}
                        id={`feature-${feature.id}`}
                        onChange={event => toggleFeature(feature.id, event.target.checked)}
                        type="switch"
                      />
                    </td>
                  </tr>
                );
              });

              if (!section.labelKey) {
                return rows;
              }

              return [
                <tr key={`${section.id}-section`}>
                  <th className={styles.section} colSpan={4} scope="colgroup">
                    {t(section.labelKey)}
                  </th>
                </tr>,
                ...rows,
              ];
            })
          }
        </tbody>
      </Table>
    </>
  );
};

export default FeaturesPage;
